// In src/pages/PrayerWall.js

import React, { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import { db } from '../services/firebase';
// MODIFIED: Added addDoc to the Firestore imports
import { collection, query, where, onSnapshot, addDoc, doc, deleteDoc, serverTimestamp } from 'firebase/firestore'; 
import { getFunctions, httpsCallable } from 'firebase/functions'; // NEW IMPORTS

// Import our components
import PrayerRequestCard from '../components/PrayerRequestCard';
import PrayerRequestModal from '../components/PrayerRequestModal';

import { Button, Container, Typography, CircularProgress, Box } from '@mui/material';
import AddIcon from '@mui/icons-material/Add';

function PrayerWall() {
  const { currentUser, userProfile } = useAuth();
  const [loading, setLoading] = useState(true);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isModalOpen, setIsModalOpen] = useState(false);

  const [publicPrayers, setPublicPrayers] = useState([]);
  const [myPrivatePrayers, setMyPrivatePrayers] = useState([]);
  const [allPrivatePrayers, setAllPrivatePrayers] = useState([]);

  // Get callable function reference
  const functions = getFunctions();
  const prayForRequestFn = httpsCallable(functions, 'prayForRequest'); // NEW: Callable function reference

  useEffect(() => {
    if (!userProfile?.churchId || !currentUser?.uid) {
      setLoading(false);
      return;
    }

    const prayersColRef = collection(db, 'churches', userProfile.churchId, 'prayers');
    // Client-side logic for viewing all private prayers (if permission is granted)
    const canViewAllPrivatePrayersFromClient = userProfile.canViewPrivatePrayers === true || userProfile.role === 'Super Administrator';

    const publicQuery = query(prayersColRef, where('isPrivate', '==', false));
    const unsubscribePublic = onSnapshot(publicQuery, (snapshot) => {
      setPublicPrayers(snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() })));
      setLoading(false);
    }, (error) => {
      console.error("Error fetching public prayers: ", error);
      setLoading(false);
    });

    let unsubscribeMyPrivate = () => {};
    let unsubscribeAllPrivate = () => {};

    // MODIFIED: Always subscribe to the user's OWN private prayers.
    // A user should always be able to see their own requests regardless of other permissions.
    const myPrivateQuery = query(
      prayersColRef,
      where('isPrivate', '==', true),
      where('authorId', '==', currentUser.uid)
    );
    unsubscribeMyPrivate = onSnapshot(myPrivateQuery, (snapshot) => {
      setMyPrivatePrayers(snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() })));
    });


    // Subscribe to ALL private prayers only if the user has the global view permission.
    if (canViewAllPrivatePrayersFromClient) {
      const allPrivateQuery = query(prayersColRef, where('isPrivate', '==', true));
      unsubscribeAllPrivate = onSnapshot(allPrivateQuery, (snapshot) => {
        setAllPrivatePrayers(snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() })));
      });
    }

    return () => {
      unsubscribePublic();
      unsubscribeMyPrivate(); // Ensure this is always unsubscribed
      unsubscribeAllPrivate();
    };
  }, [userProfile, currentUser]);

  const prayers = [...publicPrayers, ...myPrivatePrayers, ...allPrivatePrayers]
    .reduce((acc, current) => {
        if (!acc.find(item => item.id === current.id)) {
            acc.push(current);
        }
        return acc;
    }, [])
    .sort((a, b) => (b.createdAt?.toDate() || 0) - (a.createdAt?.toDate() || 0));

  const handleSavePrayer = async (prayerData) => {
    if (!userProfile?.churchId) return;
    setIsSubmitting(true);
    try {
      const prayersColRef = collection(db, 'churches', userProfile.churchId, 'prayers');
      await addDoc(prayersColRef, {
        ...prayerData,
        authorName: prayerData.isAnonymous ? 'Anonymous' : userProfile.firstName,
        authorId: currentUser.uid,
        prayerCount: 0,
        prayedByUids: [], // NEW: Initialize prayedByUids array
        createdAt: serverTimestamp(),
      });
      setIsModalOpen(false);
    } catch (error) {
      console.error("Error submitting prayer:", error);
      alert(`Failed to submit prayer: ${error.message}`);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleDeletePrayer = async (id) => {
    if (!userProfile?.churchId) return;
    if (window.confirm('Are you sure you want to delete this prayer request?')) {
      const docRef = doc(db, 'churches', userProfile.churchId, 'prayers', id);
      await deleteDoc(docRef);
    }
  };

  // MODIFIED: handlePray to call the Cloud Function
  const handlePray = async (prayerId) => {
    if (!currentUser?.uid || !userProfile?.churchId) {
      alert("You must be logged in to pray for a request.");
      return;
    }
    try {
      const result = await prayForRequestFn({ prayerId, churchId: userProfile.churchId });
      // The Cloud Function handles the check and update.
      // You can use the result for client-side feedback if needed.
      if (result.data.message === "Already prayed for this request.") {
        alert(result.data.message);
      } else {
        // No alert needed here as the UI will update from the snapshot listener
        // alert(result.data.message); // Optional: if you want a success alert
      }
    } catch (error) {
      console.error("Error calling prayForRequest function:", error);
      alert(`Failed to pray for request: ${error.message}`);
    }
  };

  if (loading) {
    return <CircularProgress />;
  }

  if (!userProfile) {
    return <Container><Typography>Could not load user profile.</Typography></Container>;
  }

  return (
    <Container>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
        <Typography variant="h4">Prayer Wall</Typography>
        <Button variant="contained" startIcon={<AddIcon />} onClick={() => setIsModalOpen(true)}>
          Submit a Request
        </Button>
      </Box>

      {!prayers.length && !loading && (
        <Typography>Be the first to submit a prayer request.</Typography>
      )}

      {prayers.map(prayer => (
        <PrayerRequestCard
          key={prayer.id}
          prayer={prayer}
          currentUser={currentUser} // Pass currentUser to enable conditional disabling
          userRole={userProfile?.role}
          onDelete={handleDeletePrayer}
          onPray={handlePray}
        />
      ))}

      <PrayerRequestModal
        open={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        onSave={handleSavePrayer}
        loading={isSubmitting}
      />
    </Container>
  );
}

export default PrayerWall;